import React, { useEffect, useState } from "react";
import { Pressable, StyleSheet, TextInput, View } from "react-native";
import {
  ArrowCounterClockwiseIcon,
  CheckIcon,
  FileTextIcon,
  PencilSimpleIcon,
  SparkleIcon,
  XIcon,
} from "@phosphor-icons/react";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";

const AI_REVISION_PROMPTS = [
  "Make it measurable",
  "Bloom's Apply level",
  "Shorter / tighter wording",
  "Stronger / more rigorous",
];

type CourseModernizerOutcomeItemProps = {
  label: string;
  text: string;
  canDelete?: boolean;
  canEdit?: boolean;
  canReviseWithAI?: boolean;
  disabled?: boolean;
  density?: "regular" | "compact";
  initialMode?: "view" | "edit" | "revise";
  onCancelEdit?: () => void;
  onSave: (text: string) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  onReviseWithAI: (instruction: string, selectedOptions: string[]) => Promise<void> | void;
  onRevertAIRevision?: () => Promise<void> | void;
  aiBadgeLabel?: string | null;
  canRevertAIRevision?: boolean;
  sourceLabel?: string | null;
};

function CourseModernizerOutcomeItem({
  label,
  text,
  canDelete = true,
  canEdit = true,
  canReviseWithAI = true,
  disabled = false,
  density = "regular",
  initialMode = "view",
  onCancelEdit,
  onSave,
  onDelete,
  onReviseWithAI,
  onRevertAIRevision,
  aiBadgeLabel,
  canRevertAIRevision = false,
  sourceLabel,
}: CourseModernizerOutcomeItemProps) {
  const [mode, setMode] = useState(initialMode);
  const [draftText, setDraftText] = useState(text);
  const [revisionInstruction, setRevisionInstruction] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit") {
      setDraftText(text);
    }
  }, [mode, text]);

  useEffect(() => {
    if ((mode === "edit" && !canEdit) || (mode === "revise" && !canReviseWithAI)) {
      setMode("view");
    }
  }, [canEdit, canReviseWithAI, mode]);

  const resetToView = () => {
    setDraftText(text);
    setRevisionInstruction("");
    setSelectedPrompt(null);
    setMode("view");
    onCancelEdit?.();
  };

  const handleSave = async () => {
    const nextText = draftText.trim();
    if (!nextText || nextText === text) {
      resetToView();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(nextText);
      setMode("view");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePromptPress = (prompt: string) => {
    setSelectedPrompt(prompt);
    setRevisionInstruction(prompt);
  };

  const handleRevise = async () => {
    const instruction = revisionInstruction.trim() || selectedPrompt || "";
    setIsSaving(true);
    try {
      await onReviseWithAI(instruction, selectedPrompt ? [selectedPrompt] : []);
      setMode("view");
    } finally {
      setIsSaving(false);
    }
  };

  const runItemAction = async (action: (() => Promise<void> | void) | undefined) => {
    if (!action) return;
    setIsSaving(true);
    try {
      await action();
    } finally {
      setIsSaving(false);
    }
  };

  const renderRevertAction = () =>
    canRevertAIRevision && onRevertAIRevision ? (
      <TextButton
        label={isSaving ? "Reverting" : "Revert"}
        density={density}
        disabled={disabled || isSaving}
        onPress={() => void runItemAction(onRevertAIRevision)}
        icon={<ArrowCounterClockwiseIcon color="#64748B" size={16} />}
      />
    ) : null;

  const renderStatusBadge = () => (aiBadgeLabel ? <StatusBadge label={aiBadgeLabel} /> : null);
  const isAiRevised = aiBadgeLabel === "AI-revised";
  const isEdited = aiBadgeLabel === "Edited";
  const hasViewActions =
    (canRevertAIRevision && Boolean(onRevertAIRevision)) || canEdit || canReviseWithAI || canDelete;
  const hasStatusBadge = Boolean(aiBadgeLabel);

  const renderSourceLabel = () =>
    sourceLabel ? (
      <View style={styles.sourceRow}>
        <FileTextIcon color="#64748B" size={13} weight="regular" />
        <CustomText text={sourceLabel} size="xs" useTranslationText={false} style={styles.sourceText} />
      </View>
    ) : null;

  if (mode === "edit" && canEdit) {
    return (
      <View
        style={[
          styles.card,
          density === "compact" && styles.compactCard,
          isAiRevised && styles.aiRevisedCard,
          isEdited && styles.editedCard,
        ]}>
        <View style={styles.editRow}>
          <OutcomeBadge label={label} density={density} />
          <TextInput
            accessibilityLabel={`Edit ${label}`}
            autoFocus
            multiline
            value={draftText}
            onChangeText={setDraftText}
            style={[styles.editInput, density === "compact" && styles.compactEditInput]}
          />
        </View>
        <View style={styles.actionRow}>
          {renderRevertAction()}
          <TextButton
            label="Cancel"
            density={density}
            disabled={isSaving || disabled}
            onPress={resetToView}
            icon={<XIcon color="#334155" size={16} />}
          />
          <PrimaryButton
            label={isSaving ? "Saving" : "Save"}
            density={density}
            disabled={isSaving || disabled}
            onPress={handleSave}
          />
        </View>
      </View>
    );
  }

  if (mode === "revise" && canReviseWithAI) {
    return (
      <View
        style={[
          styles.card,
          density === "compact" && styles.compactCard,
          isAiRevised && styles.aiRevisedCard,
          isEdited && styles.editedCard,
        ]}>
        <View style={styles.readRow}>
          <OutcomeBadge label={label} density={density} />
          <View style={styles.outcomeCopy}>
            <CustomText
              text={text}
              size="m"
              useTranslationText={false}
              style={[styles.outcomeText, density === "compact" && styles.compactOutcomeText]}
            />
            {renderSourceLabel()}
            {renderStatusBadge()}
          </View>
        </View>
        <View style={[styles.revisePanel, density === "compact" && styles.compactRevisePanel]}>
          <CustomText
            text="HOW SHOULD AI REVISE THIS OUTCOME?"
            size="s"
            weight="bold"
            useTranslationText={false}
            style={styles.reviseTitle}
          />
          <View style={styles.promptRow}>
            {AI_REVISION_PROMPTS.map((prompt) => (
              <Pressable
                key={prompt}
                accessibilityRole="button"
                accessibilityLabel={prompt}
                disabled={disabled || isSaving}
                onPress={() => handlePromptPress(prompt)}
                style={[styles.promptChip, selectedPrompt === prompt && styles.promptChipSelected]}>
                <CustomText text={prompt} size="s" useTranslationText={false} style={styles.promptText} />
              </Pressable>
            ))}
          </View>
          <TextInput
            accessibilityLabel={`AI revision notes for ${label}`}
            multiline
            value={revisionInstruction}
            onChangeText={setRevisionInstruction}
            placeholder='Or describe the change in your own words — e.g., "emphasize critical analysis, not just identification"'
            placeholderTextColor="#64748B"
            style={styles.reviseInput}
          />
          <View style={styles.actionRow}>
            {renderRevertAction()}
            <TextButton
              label="Cancel"
              density={density}
              disabled={isSaving || disabled}
              onPress={resetToView}
              icon={<XIcon color="#334155" size={15} />}
            />
            <PrimaryButton
              label={isSaving ? "Revising" : "Revise with AI"}
              density={density}
              disabled={isSaving || disabled}
              onPress={handleRevise}
              icon={<SparkleIcon color="#FFFFFF" size={16} weight="bold" />}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.card,
        density === "compact" && styles.compactCard,
        isAiRevised && styles.aiRevisedCard,
        isEdited && styles.editedCard,
      ]}>
      <View style={styles.readRow}>
        <OutcomeBadge label={label} density={density} />
        <View style={styles.outcomeCopy}>
          <CustomText
            text={text}
            size="m"
            useTranslationText={false}
            style={[styles.outcomeText, density === "compact" && styles.compactOutcomeText]}
          />
          {renderSourceLabel()}
        </View>
      </View>
      {hasStatusBadge || hasViewActions ? (
        <View style={styles.metaActionRow}>
          <View style={styles.statusBadgeSlot}>{renderStatusBadge()}</View>
          <View style={styles.viewActions}>
            {renderRevertAction()}
            {canEdit ? (
              <TextButton
                label="Edit"
                density={density}
                disabled={disabled}
                onPress={() => setMode("edit")}
                icon={<PencilSimpleIcon color="#334155" size={16} />}
              />
            ) : null}
            {canReviseWithAI ? (
              <TextButton
                label="AI revise"
                density={density}
                disabled={disabled}
                onPress={() => setMode("revise")}
                icon={<SparkleIcon color="#1D4ED8" size={16} weight="bold" />}
                tone="blue"
              />
            ) : null}
            {canDelete ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Delete ${label}`}
                disabled={disabled}
                onPress={onDelete}
                style={styles.deleteButton}>
                <XIcon color="#64748B" size={16} />
              </Pressable>
            ) : null}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function StatusBadge({ label }: { label: string }) {
  const isEdited = label === "Edited";
  return (
    <View style={[styles.statusBadge, isEdited ? styles.editedBadge : styles.aiBadge]}>
      {isEdited ? null : <SparkleIcon color="#1D4ED8" size={12} weight="bold" />}
      <CustomText
        text={label}
        size="xs"
        weight={isEdited ? "regular" : "bold"}
        useTranslationText={false}
        style={[styles.statusBadgeText, isEdited ? styles.editedBadgeText : styles.aiBadgeText]}
      />
    </View>
  );
}

function OutcomeBadge({ label, density }: { label: string; density: "regular" | "compact" }) {
  return (
    <View style={[styles.badge, density === "compact" && styles.compactBadge]}>
      <CustomText
        text={label}
        size="s"
        weight="bold"
        useTranslationText={false}
        style={[styles.badgeText, density === "compact" && styles.compactBadgeText]}
      />
    </View>
  );
}

function TextButton({
  icon,
  label,
  tone = "default",
  density = "regular",
  disabled,
  onPress,
}: {
  icon: React.ReactNode;
  label: string;
  tone?: "default" | "blue";
  density?: "regular" | "compact";
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled}
      onPress={onPress}
      style={[styles.textButton, disabled && styles.disabled]}>
      {icon}
      <CustomText
        text={label}
        size="m"
        useTranslationText={false}
        style={[
          tone === "blue" ? styles.blueText : styles.textButtonLabel,
          density === "compact" && styles.compactTextButtonLabel,
        ]}
      />
    </Pressable>
  );
}

function PrimaryButton({
  icon,
  label,
  density = "regular",
  disabled,
  onPress,
}: {
  icon?: React.ReactNode;
  label: string;
  density?: "regular" | "compact";
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <CustomButton
      text={label}
      useTranslationText={false}
      styleType="primary"
      size={density === "compact" ? "xs" : "s"}
      bold={true}
      leftIcon={icon ?? <CheckIcon color="#FFFFFF" size={16} weight="bold" />}
      disabled={disabled}
      onPress={onPress}
      accessibilityLabel={label}
      style={[styles.primaryButton, density === "compact" && styles.compactPrimaryButton, disabled && styles.disabled]}
      textStyle={[styles.primaryButtonText, density === "compact" && styles.compactPrimaryButtonText]}
    />
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    padding: 14,
    gap: 18,
  },
  compactCard: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 14,
  },
  editedCard: {
    backgroundColor: "#FFFEFB",
  },
  aiRevisedCard: {
    backgroundColor: "#F5F7FF",
  },
  readRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  editRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  badge: {
    minWidth: 42,
    height: 25,
    borderRadius: 5,
    backgroundColor: "#E7EDFF",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 7,
  },
  compactBadge: {
    minWidth: 36,
    height: 22,
    borderRadius: 4,
    paddingHorizontal: 7,
  },
  badgeText: {
    color: "#003CB3",
    lineHeight: 18,
  },
  compactBadgeText: {
    fontSize: 12,
    lineHeight: 16,
  },
  outcomeText: {
    color: "#0F1F44",
    fontSize: 19,
    lineHeight: 31,
  },
  outcomeCopy: {
    flex: 1,
    gap: 7,
  },
  compactOutcomeText: {
    fontSize: 16,
    lineHeight: 24,
  },
  metaActionRow: {
    marginLeft: 50,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
  },
  statusBadgeSlot: {
    minHeight: 24,
    justifyContent: "center",
  },
  sourceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  sourceText: {
    color: "#64748B",
    fontSize: 12,
    lineHeight: 18,
  },
  viewActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 18,
    paddingRight: 2,
    flexWrap: "wrap",
  },
  textButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minHeight: 25,
  },
  textButtonLabel: {
    color: "#1F2A44",
    fontSize: 16,
  },
  blueText: {
    color: "#003CB3",
    fontSize: 16,
  },
  compactTextButtonLabel: {
    fontSize: 13,
  },
  deleteButton: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  editInput: {
    flex: 1,
    minHeight: 106,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: "#020617",
    fontSize: 19,
    lineHeight: 31,
    textAlignVertical: "top",
    outlineStyle: "none" as never,
  },
  compactEditInput: {
    minHeight: 86,
    fontSize: 13,
    lineHeight: 20,
  },
  actionRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 18,
  },
  primaryButton: {
    minHeight: 34,
    borderRadius: 7,
    paddingHorizontal: 15,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    shadowColor: "#1D4ED8",
    shadowOpacity: 0.18,
    shadowRadius: 8,
  },
  compactPrimaryButton: {
    minHeight: 30,
    paddingHorizontal: 12,
  },
  primaryButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
  },
  compactPrimaryButtonText: {
    fontSize: 13,
  },
  revisePanel: {
    marginLeft: 59,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    backgroundColor: "#F4F7FF",
    paddingHorizontal: 15,
    paddingTop: 15,
    paddingBottom: 14,
    gap: 14,
  },
  compactRevisePanel: {
    marginLeft: 45,
  },
  reviseTitle: {
    color: "#003CB3",
    letterSpacing: 0.2,
  },
  promptRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  promptChip: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    backgroundColor: "#FFFFFF",
    borderRadius: 999,
    minHeight: 29,
    paddingHorizontal: 11,
    justifyContent: "center",
  },
  promptChipSelected: {
    borderColor: "#93C5FD",
    backgroundColor: "#EFF6FF",
  },
  promptText: {
    color: "#003CB3",
    fontSize: 14,
  },
  reviseInput: {
    minHeight: 71,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 5,
    backgroundColor: "#FFFFFF",
    color: "#0F172A",
    fontSize: 16,
    lineHeight: 24,
    paddingHorizontal: 11,
    paddingVertical: 10,
    textAlignVertical: "top",
    outlineStyle: "none" as never,
  },
  disabled: {
    opacity: 0.5,
  },
  statusBadge: {
    alignSelf: "flex-start",
    borderRadius: 999,
    paddingHorizontal: 8,
    minHeight: 22,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  aiBadge: {
    borderWidth: 1,
    borderColor: "#C9D7FF",
    backgroundColor: "#E7EDFF",
  },
  editedBadge: {
    backgroundColor: "#FFF4E8",
  },
  statusBadgeText: {
    fontSize: 12,
  },
  aiBadgeText: {
    color: "#1D4ED8",
  },
  editedBadgeText: {
    color: "#C25B00",
  },
});

export default CourseModernizerOutcomeItem;
